import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { PartyPopper } from 'lucide-react';

import type { RxSubmissionState } from '../../model/submission/orchestrator';

export interface ConfirmationStepProps {
  submissionState: RxSubmissionState;
  onRetryCreate: () => Promise<void>;
  onRetrySend: () => Promise<void>;
  /**
   * True when this submission resulted in a prior authorization rather than
   * a pharmacy send. Post-submit this reflects the backend's actual
   * disposition (via ``submissionState.sendDisposition``); pre-submit it
   * falls back to the preview-gate outcome. Drives "Prior auth sent" copy
   * across the step, including the retry-send button label.
   */
  isPriorAuth?: boolean;
  /** True when the drug was excluded from coverage — the Rx is signed in
   *  DoseSpot but diverted to a clinician-action request instead of going
   *  to the pharmacy. */
  isExcluded?: boolean;
  /** PA.132.1 / PA.133: whether the PA was submitted with expedited priority. */
  expedited?: boolean;
  /**
   * The DoseSpot ``PriorAuthorizationCaseId`` when ``/priorAuths/initiate``
   * echoed one back. ``null`` either means the backend accepted the initiate
   * but hasn't received the case number yet (the poller will hydrate it) or
   * this wasn't a PA submission at all.
   */
  priorAuthCaseNumber?: string | null;
}

export function ConfirmationStep({
  submissionState,
  onRetryCreate,
  onRetrySend,
  isPriorAuth = false,
  isExcluded = false,
  expedited = false,
  priorAuthCaseNumber = null,
}: ConfirmationStepProps) {
  const { status, dosespotPrescriptionId, errorMessage } = submissionState;

  const successTitle = isPriorAuth
    ? 'Prior auth sent'
    : isExcluded
      ? 'Prescription signed'
      : 'Prescription sent';
  const successBody = isPriorAuth
    ? "We started a prior authorization with the patient's payer instead of sending to the pharmacy. We'll notify the team when the payer responds."
    : isExcluded
      ? 'This drug appears to be excluded from coverage. The prescription was signed but not sent — check the inbox for next steps.'
      : "We sent this prescription to the patient's pharmacy on file. We'll notify them if anything changes.";

  const failedSendHint = isPriorAuth
    ? 'Prescription created but prior auth could not be initiated.'
    : 'Prescription created but not sent.';
  const failedCreateHint = 'Prescription could not be created.';

  const failedSendAlertTitle = isPriorAuth ? 'Created, PA not sent' : 'Created, not sent';
  const failedSendAlertBody = isPriorAuth
    ? 'The prescription was created but the prior authorization could not be initiated.'
    : 'The prescription was created but could not be sent.';

  return (
    <div className="flex flex-col gap-4">
      {status === 'success' ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 text-sm font-medium">
            <PartyPopper className="size-4" aria-hidden />
            {successTitle}
          </div>
          <div className="text-sm text-muted-foreground">{successBody}</div>
          {isPriorAuth && priorAuthCaseNumber ? (
            <div className="text-sm text-muted-foreground">
              PA case: <span className="font-mono text-foreground">#{priorAuthCaseNumber}</span>
            </div>
          ) : null}
          {(isPriorAuth || isExcluded) && expedited ? (
            <div className="text-sm font-medium text-amber-700 dark:text-amber-300">
              Expedited priority requested
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-sm font-medium">Confirmation</div>
            <div className="text-sm text-muted-foreground">
              {status === 'failed-send'
                ? failedSendHint
                : status === 'failed-create'
                  ? failedCreateHint
                  : 'Review the outcome below.'}
            </div>
          </div>

          {dosespotPrescriptionId ? (
            <div className="text-sm text-muted-foreground">
              DoseSpot Rx ID:{' '}
              <span className="font-mono text-foreground">{dosespotPrescriptionId}</span>
            </div>
          ) : null}
        </>
      )}

      {status === 'failed-send' ? (
        <Alert variant="destructive">
          <AlertTitle>{failedSendAlertTitle}</AlertTitle>
          <AlertDescription>
            <div>{errorMessage ?? failedSendAlertBody}</div>
            <div className="pt-3">
              <Button
                type="button"
                radius="pill"
                onClick={() => void onRetrySend()}
                disabled={!dosespotPrescriptionId}
              >
                {isPriorAuth ? 'Retry send PA' : 'Retry send'}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      {status === 'failed-create' ? (
        <Alert variant="destructive">
          <AlertTitle>Create failed</AlertTitle>
          <AlertDescription>
            <div>{errorMessage ?? 'The prescription could not be created.'}</div>
            <div className="pt-3">
              <Button type="button" radius="pill" onClick={() => void onRetryCreate()}>
                Retry create
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
